import React from "react";
import classnames from "classnames";
import diffDays from "date-fns/differenceInDays";
import diffText from "date-fns/formatDistanceStrict";
import parse from "date-fns/parseISO";
import startOfDay from "date-fns/startOfDay";
import { ModalHeader, ModalBody } from "reactstrap";
import { Clock, Mail, Link } from "react-feather";

import { longDate } from "../../../common/helpers/date";

import { MarketingActivity as Activity, MarketingObjective as Objective } from "../../../api";

interface ActivityModalContentProps {
  activity: Activity;
  objectivesById: Record<string, Objective | undefined>;
  showLinks: boolean;
  handleSelectObjective: (id: string) => any;
}

const emailAddress = process.env.REACT_APP_MARKETING_EMAIL_ADDRESS ?? "";

const ActivityModalContent: React.FC<ActivityModalContentProps> = ({
  activity,
  objectivesById,
  showLinks,
  handleSelectObjective,
}) => {

  const today = startOfDay(new Date());
  const deadlineDate = activity.deadlineDate ? parse(activity.deadlineDate) : today;
  const deadlineDays = diffDays(deadlineDate, today);
  const isOverdue = deadlineDays < 0;
  const deadlineText = isOverdue
    ? `${diffText(today, deadlineDate)} overdue`
    : `${diffText(deadlineDate, today)} left`;

  return (
    <>
      <ModalHeader>
        <span className="text-primary">{activity.name}</span>
      </ModalHeader>

      {!!activity.deadlineDate && activity.isActive && (
        <ModalBody className={classnames({
          "d-flex align-items-center py-2": true,
          "bg-danger text-light": deadlineDays < 0,
          "bg-neutral text-light": 0 < deadlineDays,
        })}>
          <Clock size={15} />
          <span className="ml-2">{deadlineText}</span>
        </ModalBody>)}

      <ModalBody>
        {/* Status */}
        <div className="d-flex justify-content-between align-items-center">
          <strong>Status</strong>
          <div className="ml-1">{activity.status}</div>
        </div>
        {/* Publish date */}
        <div className="mt-2 d-flex justify-content-between align-items-center">
          <strong>Publication Live</strong>
          <div>
            {!!activity.publicationDate
              ? longDate(parse(activity.publicationDate))
              : "-"}
          </div>
        </div>
        {/* Content deadline */}
        <div className="mt-2 d-flex justify-content-between align-items-center">
          <strong>Content Deadline</strong>
          <div>
            <div>
              {!!activity.deadlineDate
                ? longDate(deadlineDate)
                : "-"}
            </div>
          </div>
        </div>
        {/* Completion date */}
        {!!activity.completedDate && (
          <div className="mt-2 d-flex justify-content-between align-items-center">
            <strong>Completed</strong>
            <div>
              {!!activity.completedDate
                ? longDate(parse(activity.completedDate))
                : "-"}
            </div>
          </div>)}
        {/* Email */}
        {!!activity.isActive && (
          <div className="mt-3 d-flex justify-content-end">
            <div>
              <a href={`mailto:${emailAddress}?subject=${activity.name}`}
                className="btn btn-sm btn-outline-neutral d-flex align-items-center">
                <Mail size={15} />
                <span className="ml-1">Email content</span>
              </a>
            </div>
          </div>)}

      </ModalBody>

      {/* Objectives */}
      {showLinks && !!activity.objectiveIds?.length && (
        <ModalBody className="pt-0">
          <div className="mb-2">
            <strong>Objectives</strong>
          </div>
          <ul className="m-0 p-0">
            {activity.objectiveIds
              .filter(objectiveId => objectiveId in objectivesById)
              .map(objectiveId => (
                <li key={objectiveId} className="mt-1 d-flex">
                  <Link size={15} />
                  <span className="ml-1">
                    <a href={`#${objectiveId}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSelectObjective(objectiveId);
                      }}>{objectivesById[objectiveId]?.name}</a>
                  </span>
                </li>
              ))}
          </ul>
        </ModalBody>
      )}
    </>
  )
}

export default ActivityModalContent;
